import React, { useEffect, useState, useRef, forwardRef } from "react";
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { compose } from 'redux';
import withWindowResizeHandler from "../../hocs/with_window_resize_handler.js";

const blockStyles = makeStyles(theme => ({
  container: {
    overflow: "hidden",
    position: "relative",
  },
  image: {
    position: "absolute",
    left: "-10000%",
    right: "-10000%", 
    top: "-10000%",
    bottom: "-10000%",
    margin: "auto auto",
    width: "auto",
    height: "auto",
  },
  contain: {
    maxWidth: "10%",
    maxHeight: "10%",
    transform: "scale(10)",
    objectFit: "contain"
  },
  cover: {
    minWidth: "1000%",
    minHeight: "1000%",
    transform: "scale(0.1)",
    objectFit: "cover"
  }
}));

export const ScaleModeCover   = "cover";
export const ScaleModeContain = "contain";

export const BasicDimensionWidth  = "BasicDimensionWidth";
export const BasicDimensionHeight = "BasicDimensionHeight";

/**
 * Component for display image from src or src set. Support content mode (cover and contain)
 * @name Image
 * @param {object} props React component properties
 * @param {string} [props.className] CSS-class of container
 * @param {func} [props.ref] Ref
 * @param {string} [props.src] Url string with path to image
 * @param {object.<string, string>} [props.srcSet = {}] Src set with image urls for special resolutions (for example high resolution images for retina screen)
 * @param {string} [props.scaleMode = "cover"] Content mode for display image. Cross browser analog of "object-fit" img attribute. Available values:
 *  - "cover"
 *  - "contain"
 * @example
 * return (
 *  <Image className = { "image-class" }
 *         scaleMode = "contain" 
 *         src = { "https://www.images.com/my-image.jpg" } 
 *         srcSet = { {
 *          "@2x": "https://www.images.com/my-image@2x.jpg",
 *          "@3x": "https://www.images.com/my-image@3x.jpg"
 *         } }/>
 * )
*/

const Image = forwardRef((props, ref) => {
  const classes = blockStyles();

  const { width, height, style, imgWidth, imgHeight } = props;
  const { className, src, alt = '', onClick, basicDimension } = props;
  const { scaleMode = ScaleModeCover, srcSet = {} } = props;

  const imageRef = useRef();

  const [containerWidth, setContainerWidth] = useState(undefined);
  const [containerHeight, setContainerHeight] = useState(undefined);

  const srcSetString = Object.keys(srcSet).map((key) => {
    const url = srcSet[key];
    return `${url} ${key}`;
  }).join(", ");

  const updateDims = () => {
    if(!basicDimension) {
      setContainerWidth(undefined);
      setContainerHeight(undefined);
      return;
    }

    if(basicDimension == BasicDimensionHeight) {
      reloadContainerWidth(height);
      return;
    }

    if(basicDimension == BasicDimensionWidth) {
      reloadContainerHeight(width);
      return;
    }
  }

  const reloadContainerWidth = (height) => {
    const { current: { width: imageWidth, height: imageHeight } = {} } = imageRef;
    if(imageWidth === undefined || imageHeight === undefined) {
      return;
    }
    const newWidth = height * imageWidth / imageHeight;
    setContainerWidth(newWidth);
  }

  const reloadContainerHeight = (width) => {
    const { current: { width: imageWidth, height: imageHeight } = {} } = imageRef;
    if(imageWidth === undefined || imageHeight === undefined) {
      return;
    }
    const newHeight = width * imageHeight / imageWidth;
    setContainerHeight(newHeight);
  }

  const onLoad = ({ target: img }) => {
    imageRef.current = {
      width: img.offsetWidth,
      height: img.offsetHeight,
    }
    updateDims();
  }

  useEffect(() => {
    updateDims();
  }, [basicDimension, props.width, props.height]);

  useEffect(() => {
    const { onUpdateMetrics } = props;
    if(onUpdateMetrics) {
      onUpdateMetrics(containerWidth || width, containerHeight || height);
    }
  }, [containerWidth, containerHeight]);

  const totalStyle = {
    ...style,
    width: imgWidth || containerWidth,
    height: imgHeight || containerHeight
  }

  return (
    <div className = { clsx({
      [classes.container]: true, 
      [className]: true}) }
                ref = { ref }
                onClick = { onClick }
                style = { totalStyle } >
      <img src = { src } 
           alt = { alt }
           srcSet = { srcSetString }
           onLoad = { onLoad }
           className = { clsx({
             [classes.image]: true, 
             [classes.cover]: scaleMode === ScaleModeCover,
             [classes.contain]: scaleMode === ScaleModeContain}) } />
    </div>
  );
});

Image.propTypes = {
  scaleMode: PropTypes.oneOf([ScaleModeCover, ScaleModeContain]),
  basicDimension: PropTypes.oneOf([BasicDimensionWidth, BasicDimensionHeight]),
  src: PropTypes.string,
  alt: PropTypes.string,
  srcSet: PropTypes.object,
  className: PropTypes.string
};

export default compose(withWindowResizeHandler)(Image);
